import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import INDIAN_STATES from '../../constants/indianStates';
import {
  ArrowLeft,
  MapPin,
  Plus,
  Trash2,
  Save,
  Building2,
  Locate
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';

// Import Leaflet
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Helper function to round coordinates to 6 decimal places
const roundCoordinate = (coord) => {
  return parseFloat(parseFloat(coord).toFixed(6));
};

const CreateRegionalOffice = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [locating, setLocating] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    state: '',
    location: null,
    contactInfo: {
      phone: '',
      email: '',
      website: ''
    },
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    servingAreas: []
  });

  const createMutation = useMutation(
    (data) => api.post('/regional-offices', data),
    {
      onSuccess: () => {
        toast.success('Regional office created successfully');
        navigate('/regional-offices');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to create regional office');
      }
    }
  );

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleLocationSelect = (lat, lng) => {
    const roundedLat = roundCoordinate(lat);
    const roundedLng = roundCoordinate(lng);
    
    setFormData(prev => ({
      ...prev,
      location: [roundedLng, roundedLat] // GeoJSON format: [longitude, latitude]
    }));

    // Add marker to map
    if (map) {
      // Clear existing markers
      markers.forEach(marker => map.removeLayer(marker));
      
      const newMarker = L.marker([roundedLat, roundedLng]).addTo(map);
      setMarkers([newMarker]);
    }
  };

  const locateUser = () => {
    if (!map) return;
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        map.flyTo([position.coords.latitude, position.coords.longitude], 13);
        setLocating(false);
      },
      () => {
        toast.error('Unable to get your location. Please allow location access in your browser.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const startDrawingPolygon = () => {
    setIsDrawingPolygon(true);
    setCurrentPolygon([]);
    // Clear any existing temporary markers
    if (map) {
      markers.forEach(marker => {
        if (marker._tempPolygonMarker) {
          map.removeLayer(marker);
        }
      });
    }
  };

  const addPointToPolygon = (lat, lng) => {
    if (isDrawingPolygon) {
      const roundedLat = roundCoordinate(lat);
      const roundedLng = roundCoordinate(lng);
      
      setCurrentPolygon(prev => [...prev, [roundedLng, roundedLat]]);
      
      // Add temporary marker to map
      if (map) {
        const marker = L.marker([roundedLat, roundedLng], {
          icon: L.divIcon({
            className: 'temp-polygon-marker',
            html: '<div style="background-color: red; width: 10px; height: 10px; border-radius: 50%; border: 2px solid white;"></div>',
            iconSize: [10, 10]
          })
        }).addTo(map);
        marker._tempPolygonMarker = true;
        setMarkers(prev => [...prev, marker]);
      }
    }
  };

  const finishPolygon = () => {
    if (currentPolygon.length >= 3) {
      const newPolygon = {
        coordinates: [...currentPolygon, currentPolygon[0]], // Close the polygon
        name: `Area ${polygons.length + 1}`,
        description: ''
      };

      setPolygons(prev => [...prev, newPolygon]);
      setFormData(prev => ({
        ...prev,
        servingAreas: [...prev.servingAreas, newPolygon]
      }));

      // Draw polygon on map
      if (map) {
        const latLngs = currentPolygon.map(([lng, lat]) => [lat, lng]);
        const polygon = L.polygon(latLngs, { 
          color: 'blue', 
          weight: 2,
          fillColor: '#3388ff',
          fillOpacity: 0.2
        }).addTo(map);
        setMarkers(prev => [...prev, polygon]);
      }

      // Clear temporary markers
      if (map) {
        markers.forEach(marker => {
          if (marker._tempPolygonMarker) {
            map.removeLayer(marker);
          }
        });
      }
    } else {
      toast.error('A polygon must have at least 3 points');
    }

    setIsDrawingPolygon(false);
    setCurrentPolygon([]);
  };

  const removePolygon = (index) => {
    setPolygons(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      servingAreas: prev.servingAreas.filter((_, i) => i !== index)
    }));

    // Remove from map
    if (map && markers[index + 1]) { // +1 because first marker is location
      map.removeLayer(markers[index + 1]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.address || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.servingAreas.length === 0) {
      toast.error('Please add at least one serving area');
      return;
    }

    if (!formData.adminName || !formData.adminEmail || !formData.adminPassword) {
      toast.error('Please fill in admin details');
      return;
    }

    // Ensure all coordinates are properly rounded before submission
    const submissionData = {
      ...formData,
      location: formData.location ? [
        roundCoordinate(formData.location[0]),
        roundCoordinate(formData.location[1])
      ] : null,
      servingAreas: formData.servingAreas.map(area => ({
        ...area,
        coordinates: area.coordinates.map(coord => [
          roundCoordinate(coord[0]),
          roundCoordinate(coord[1])
        ])
      }))
    };

    createMutation.mutate(submissionData);
  };

  // Initialize map when component mounts
  React.useEffect(() => {
    if (!mapRef.current) return;

    // Initialize Leaflet map
    const leafletMap = L.map(mapRef.current).setView([20.5937, 78.9629], 5); // Center of India

    // Add OpenStreetMap tiles - simple configuration
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      subdomains: ['a', 'b', 'c']
    }).addTo(leafletMap);

    setMap(leafletMap);

    return () => {
      if (leafletMap) {
        leafletMap.remove();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update map click handler when drawing state changes
  React.useEffect(() => {
    if (!map) return;

    // Remove existing click handler
    map.off('click');

    // Add new click handler based on current state
    map.on('click', (e) => {
      if (!isDrawingPolygon) {
        handleLocationSelect(e.latlng.lat, e.latlng.lng);
      } else {
        addPointToPolygon(e.latlng.lat, e.latlng.lng);
      }
    });
  }, [map, isDrawingPolygon]);

  if (!hasPermission('regional_offices', 'write')) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to create regional offices.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/regional-offices')}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="page-title">Create Regional Office</h1>
                <p className="page-subtitle">Add a new regional office with serving areas</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Fields */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Basic Information</h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="form-label">Office Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="input"
                    placeholder="Enter office name"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Address *</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder="Enter office address"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">State</label>
                  <select
                    value={formData.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="input"
                  >
                    <option value="">Select state…</option>
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Drives state-wise app config for trips in this office’s area.</p>
                </div>

                <div>
                  <label className="form-label">Location *</label>
                  <div className="text-sm text-gray-500 mb-2">
                    Click on the map to select the office location
                  </div>
                  {formData.location && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {formData.location[1].toFixed(6)}, {formData.location[0].toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Contact Information</h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    value={formData.contactInfo.phone}
                    onChange={(e) => handleInputChange('contactInfo.phone', e.target.value)}
                    className="input"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={formData.contactInfo.email}
                    onChange={(e) => handleInputChange('contactInfo.email', e.target.value)}
                    className="input"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    value={formData.contactInfo.website}
                    onChange={(e) => handleInputChange('contactInfo.website', e.target.value)}
                    className="input"
                    placeholder="Enter website URL"
                  />
                </div>
              </div>
            </div>

            {/* Admin Account */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Admin Account</h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="form-label">Admin Name *</label>
                  <input
                    type="text"
                    value={formData.adminName}
                    onChange={(e) => handleInputChange('adminName', e.target.value)}
                    className="input"
                    placeholder="Enter admin name"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Admin Email *</label>
                  <input
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                    className="input"
                    placeholder="Enter admin email"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Admin Password *</label>
                  <input
                    type="password"
                    value={formData.adminPassword}
                    onChange={(e) => handleInputChange('adminPassword', e.target.value)}
                    className="input"
                    placeholder="Enter admin password"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Map and Serving Areas */}
          <div className="space-y-6">
            {/* Map */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Location & Serving Areas</h3>
              </div>
              <div className="card-body">
                <div className="mb-4 space-y-2">
                  <div className="text-sm text-gray-600">
                    <strong>Instructions:</strong>
                  </div>
                  <div className="text-sm text-gray-500">
                    1. Click "My Location" to jump to your GPS location, then click on the map to set office location
                  </div>
                  <div className="text-sm text-gray-500">
                    2. Click "Start Drawing" to create serving areas
                  </div>
                  <div className="text-sm text-gray-500">
                    3. Click on map to add polygon points
                  </div>
                  <div className="text-sm text-gray-500">
                    4. Click "Finish Polygon" to complete the area
                  </div>
                </div>

                {/* Map Mode Indicator */}
                <div className="mb-4 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${isDrawingPolygon ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                    <span className="text-sm font-medium">
                      {isDrawingPolygon ? 'Drawing Mode' : 'Location Selection Mode'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {isDrawingPolygon 
                      ? 'Click on the map to add polygon points for serving areas'
                      : 'Click on the map to set the office location'
                    }
                  </div>
                </div>

                <div className="flex space-x-2 mb-4">
                  <button
                    type="button"
                    onClick={locateUser}
                    disabled={locating}
                    className={`btn btn-sm ${locating ? 'btn-disabled' : 'btn-secondary'}`}
                    title="Jump to my current GPS location"
                  >
                    <Locate className={`h-4 w-4 mr-2 ${locating ? 'animate-spin' : ''}`} />
                    {locating ? 'Locating...' : 'My Location'}
                  </button>

                  <button
                    type="button"
                    onClick={startDrawingPolygon}
                    disabled={isDrawingPolygon}
                    className={`btn btn-sm ${isDrawingPolygon ? 'btn-disabled' : 'btn-secondary'}`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Start Drawing
                  </button>
                  
                  {isDrawingPolygon && (
                    <button
                      type="button"
                      onClick={finishPolygon}
                      disabled={currentPolygon.length < 3}
                      className={`btn btn-sm ${currentPolygon.length < 3 ? 'btn-disabled' : 'btn-success'}`}
                    >
                      Finish Polygon ({currentPolygon.length} points)
                    </button>
                  )}
                </div>

                {/* Drawing Status */}
                {isDrawingPolygon && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-700">
                        Drawing Mode Active
                      </span>
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      Click on the map to add polygon points. Current points: {currentPolygon.length}
                    </div>
                  </div>
                )}

                <div
                  ref={mapRef}
                  className="w-full h-96 rounded-lg border border-gray-300"
                ></div>
              </div>
            </div>

            {/* Serving Areas List */}
            {polygons.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Serving Areas</h3>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {polygons.map((polygon, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{polygon.name}</div>
                          <div className="text-sm text-gray-500">
                            {polygon.coordinates.length} points
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePolygon(index)}
                          className="btn btn-sm btn-danger"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/regional-offices')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="btn btn-primary"
          >
            <Save className="h-4 w-4 mr-2" />
            {createMutation.isLoading ? 'Creating...' : 'Create Office'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRegionalOffice; 